import { useState, useEffect } from 'react';
import { Calendar, Clock, Globe, CheckCircle, XCircle, Loader2, Zap } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { calendarService } from '../../services/calendarService';
import { toLocaleDateStringInTz } from '../../utils/dateFormatters';
import { useTimezone } from '../../hooks/useTimezone';
import type { CalendarConnection, CalendarProvider } from '../../types/calendar';

interface ProviderConfig {
  id: CalendarProvider;
  name: string;
  description: string;
  iconBg: string;
  iconSrc: string;
  available: boolean;
}

const PROVIDERS: ProviderConfig[] = [
  {
    id: 'google',
    name: 'Google Calendar',
    description: 'Google Workspace',
    iconBg: 'from-teal-50 to-emerald-50',
    iconSrc: '/Google-Calendar-icon.svg',
    available: true,
  },
  {
    id: 'microsoft',
    name: 'Microsoft Outlook',
    description: 'Microsoft 365',
    iconBg: 'from-blue-50 to-sky-50',
    iconSrc: '/outlook-calendar-icon.svg',
    available: true,
  },
];

const STORAGE_KEYS: Record<CalendarProvider, string> = {
  google: 'oauth-result-calendar',
  microsoft: 'oauth-result-microsoft-calendar',
};

export function CalendarsSection() {
  const { user } = useAuth();
  const tz = useTimezone();
  const [connection, setConnection] = useState<CalendarConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadConnection = async () => {
    if (!user?.client_id) return;
    try {
      setLoading(true);
      const data = await calendarService.getCalendarConnection(user.client_id, user.email);
      setConnection(data);
    } catch (err) {
      console.error('Error loading calendar connection:', err);
      setError('Failed to load connection status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConnection();
  }, [user]);

  const openOAuthPopup = (authUrl: string, storageKey: string, providerName: string) => {
    setConnecting(true);
    setError(null);
    localStorage.removeItem(storageKey);

    try {
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        authUrl,
        `${providerName}-calendar-oauth-popup`,
        `width=${width},height=${height},left=${left},top=${top}`
      );

      let timeoutId: number | null = null;
      let pollId: number | null = null;

      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        if (pollId) clearInterval(pollId);
      };

      pollId = window.setInterval(() => {
        if (popup && popup.closed) {
          const raw = localStorage.getItem(storageKey);
          if (!raw) {
            cleanup();
            setConnecting(false);
            return;
          }
        }

        const raw = localStorage.getItem(storageKey);
        if (!raw) return;

        cleanup();
        localStorage.removeItem(storageKey);

        try {
          const data = JSON.parse(raw);
          if (data.success) {
            loadConnection();
          } else {
            setError(data.error || `Failed to connect ${providerName}`);
          }
        } catch {
          setError(`Failed to connect ${providerName}`);
        }
        setConnecting(false);
      }, 500);

      timeoutId = window.setTimeout(() => {
        cleanup();
        setConnecting(false);
        setError('Connection timed out. Please try again.');
      }, 120000);
    } catch (err) {
      console.error(`Error initiating ${providerName} OAuth:`, err);
      setError('Failed to start connection process.');
      setConnecting(false);
    }
  };

  const handleConnect = async (provider: CalendarProvider) => {
    if (!user?.client_id) return;

    const storageKey = STORAGE_KEYS[provider];

    try {
      if (provider === 'google') {
        const authUrl = await calendarService.initiateGoogleOAuth(user.client_id, user.email || '');
        openOAuthPopup(authUrl, storageKey, 'Google Calendar');
      } else if (provider === 'microsoft') {
        const authUrl = await calendarService.initiateMicrosoftOAuth(user.client_id, user.email || '');
        openOAuthPopup(authUrl, storageKey, 'Microsoft Outlook');
      }
    } catch (err) {
      console.error('Error initiating calendar OAuth:', err);
      setError('Failed to start connection process.');
    }
  };

  const handleDisconnect = async () => {
    if (!user?.client_id || !connection) return;
    if (!confirm('Are you sure you want to disconnect your calendar?')) return;

    try {
      setDisconnecting(true);
      const result = connection.sales_member_id
        ? await calendarService.disconnectMemberCalendar(user.client_id, connection.sales_member_id, connection.provider)
        : await calendarService.disconnectCalendar(user.client_id, connection.provider);
      if (result.success) {
        setConnection(null);
      } else {
        setError(result.error || 'Failed to disconnect');
      }
    } catch (err) {
      console.error('Error disconnecting:', err);
      setError('Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  const activeProvider = connection
    ? PROVIDERS.find(p => p.id === connection.provider)
    : null;

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#1875FF] animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {connection && activeProvider ? (
        <ConnectedView
          connection={connection}
          provider={activeProvider}
          disconnecting={disconnecting}
          onDisconnect={handleDisconnect}
          tz={tz}
        />
      ) : (
        <ProviderPicker
          providers={PROVIDERS}
          connecting={connecting}
          onConnect={handleConnect}
        />
      )}
    </div>
  );
}

function ProviderPicker({
  providers,
  connecting,
  onConnect,
}: {
  providers: ProviderConfig[];
  connecting: boolean;
  onConnect: (provider: CalendarProvider) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 text-red-600 rounded-full text-sm font-medium">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <span>Not connected</span>
        </div>
      </div>

      <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
        <div className="flex items-start gap-3">
          <Calendar className="w-5 h-5 text-[#1875FF] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-700">
            Choose your calendar provider. SalesPad will automatically check availability and schedule meetings without conflicts.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {providers.map((provider) => (
          <div
            key={provider.id}
            className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md hover:border-gray-300 transition-all duration-200 flex flex-col"
          >
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${provider.iconBg} flex items-center justify-center mb-4`}>
              <img src={provider.iconSrc} alt={provider.name} className="w-7 h-7" />
            </div>
            <h3 className="text-base font-bold text-gray-900 font-display mb-1">{provider.name}</h3>
            <p className="text-sm text-gray-500 mb-4 flex-1">{provider.description}</p>

            {provider.available ? (
              <button
                onClick={() => onConnect(provider.id)}
                disabled={connecting}
                className="w-full px-4 py-2.5 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white font-medium rounded-lg hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {connecting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Connecting...</span>
                  </>
                ) : (
                  <span>Connect</span>
                )}
              </button>
            ) : (
              <button
                disabled
                className="w-full px-4 py-2.5 bg-gray-100 text-gray-400 font-medium rounded-lg cursor-not-allowed flex items-center justify-center gap-2"
              >
                <span>Coming Soon</span>
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ConnectedView({
  connection,
  provider,
  disconnecting,
  onDisconnect,
  tz,
}: {
  connection: CalendarConnection;
  provider: ProviderConfig;
  disconnecting: boolean;
  onDisconnect: () => void;
  tz?: string;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 bg-gradient-to-br ${provider.iconBg} rounded-xl flex items-center justify-center shadow-md`}>
              <img src={provider.iconSrc} alt={provider.name} className="w-7 h-7" />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-gray-900 font-display">{provider.name}</h3>
              <p className="text-sm text-gray-500">Smart scheduling & availability</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium">
            <div className="w-2 h-2 bg-green-500 rounded-full" />
            <span>Connected</span>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 mb-1">
              <Globe className="w-4 h-4 text-[#1875FF]" />
              <span className="text-xs font-medium text-gray-600">Account</span>
            </div>
            <p className="text-sm font-semibold text-gray-900 truncate">
              {connection.calendar_email || 'Connected'}
            </p>
          </div>

          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-[#1875FF]" />
              <span className="text-xs font-medium text-gray-600">Timezone</span>
            </div>
            <p className="text-sm font-semibold text-gray-900">{connection.timezone}</p>
          </div>

          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4 text-[#1875FF]" />
              <span className="text-xs font-medium text-gray-600">Connected</span>
            </div>
            <p className="text-sm font-semibold text-gray-900">
              {toLocaleDateStringInTz(connection.created_at, tz, 'en-US', { month: 'short', day: 'numeric' })}
            </p>
          </div>
        </div>

        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start gap-3">
            <Zap className="w-5 h-5 text-[#1875FF] flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-bold text-gray-900 font-display mb-1">Calendar Sync Active</h4>
              <p className="text-xs text-gray-600">
                Agents can automatically check availability and schedule meetings without conflicts.
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={onDisconnect}
          disabled={disconnecting}
          className="w-full px-4 py-2.5 bg-white border-2 border-red-600 text-red-600 font-medium rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {disconnecting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Disconnecting...</span>
            </>
          ) : (
            <span>Disconnect {provider.name}</span>
          )}
        </button>
      </div>
    </div>
  );
}
